import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { useFinancePin } from "@/context/FinancePinContext";

/**
 * FinancePinModal — minimalist PIN unlock prompt.
 *
 * Mounted ONCE at the app root and surfaced imperatively via
 * `useFinancePin().openPinModal()`. A single hidden numeric input
 * drives a 4–6 dot indicator so on mobile the native numeric keypad
 * pops up automatically (`inputMode="numeric"` + `autoComplete="one-time-code"`).
 *
 * Per spec, this modal does ONE thing: ask for the PIN, verify, unlock.
 * No password fallback, no "forgot PIN" link here — recovery happens
 * exclusively in Settings (where the operator can prove ownership with
 * the account password).
 */
export default function FinancePinModal() {
  const { enabled, unlocked, unlock, setOpenPinModalImpl } = useFinancePin();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  // Imperative open. setOpenPinModalImpl wraps the function in a setState
  // updater so we pass the open-fn itself (not a thunk).
  useEffect(() => {
    setOpenPinModalImpl(() => {
      setError("");
      setPin("");
      setOpen(true);
    });
  }, [setOpenPinModalImpl]);

  // Focus the hidden input each time the dialog opens to lift the
  // virtual keyboard on iPad / iPhone.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Auto-close when the unlock state flips (covers the success path and
  // also the "PIN disabled in Settings while modal was open" edge case).
  useEffect(() => { if (unlocked) setOpen(false); }, [unlocked]);
  useEffect(() => { if (!enabled) setOpen(false); }, [enabled]);

  const handlePinChange = (e) => {
    const raw = e.target.value || "";
    // Strip any non-digit that may slip in via paste / IME / autofill,
    // but real keystrokes are already blocked at keydown level below
    // so an alphanumeric password can never visibly land in the field.
    const v = raw.replace(/\D/g, "").slice(0, 6);
    setPin(v);
    if (error) setError("");
    // If the raw input contained non-digit characters (e.g. someone
    // pasted their account password rather than the numeric PIN),
    // shake the field so the operator gets a visible cue that letters
    // were rejected — instead of silently extracting the digits
    // substring (the bug that made the previous build "feel like" the
    // password unlocked).
    if (raw && raw !== v) {
      setShake(true);
      setTimeout(() => setShake(false), 380);
      setError("Solo cifre — la password non sblocca i dati.");
    }
  };

  const handleKeyDown = (e) => {
    // Submit on Enter
    if (e.key === "Enter") { handleSubmit(e); return; }
    // Allow control keys (Backspace, Tab, arrows, Delete, etc.)
    if (e.key.length !== 1) return;
    // Allow ⌘/Ctrl + V|C|A|X for paste/copy/select-all/cut
    if (e.metaKey || e.ctrlKey) return;
    // Block any non-digit keystroke. This is the spec's hard guarantee:
    // the operator's account password (which contains letters) must
    // never even *appear* in the PIN input. With this gate the modal
    // can be unlocked ONLY by typing the 4–6 digit PIN — there is no
    // way a substring of the password can sneak through anymore.
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
      setShake(true);
      setTimeout(() => setShake(false), 380);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (busy || pin.length < 4) return;
    setBusy(true);
    const r = await unlock(pin);
    setBusy(false);
    if (r.ok) {
      setPin("");
      setOpen(false);
      return;
    }
    setShake(true);
    setTimeout(() => setShake(false), 380);
    setPin("");
    setError(r.error || "PIN non corretto.");
    inputRef.current?.focus();
  };

  const slots = [0, 1, 2, 3, 4, 5];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="finance-pin-modal">
        <div className="px-7 pt-7 pb-2">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <DialogTitle className="font-serif-heading text-xl font-normal tracking-tight">
              Inserisci il PIN economico
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-neutral-600 leading-relaxed">
            Questi dati sono protetti per motivi di riservatezza.
          </DialogDescription>
        </div>

        <form onSubmit={handleSubmit} className="px-7 pb-7">
          <label htmlFor="finance-pin-input" className="sr-only">PIN economico</label>
          {/* Visible dots track + the input layered on top. The input is
              optically invisible (opacity:0) but stays in the DOM flow
              and receives clicks/keystrokes — that's what lets Playwright
              and users alike focus it by tapping the dots area. */}
          <div className={`relative w-full mt-2 mb-4 ${shake ? "animate-pulse" : ""}`}>
            <div
              className="w-full flex items-center justify-center gap-3 sm:gap-4 py-6 rounded-md border border-border bg-neutral-50"
              data-testid="finance-pin-dots-trigger"
              aria-hidden="true"
            >
              {slots.map((i) => {
                const filled = i < pin.length;
                return (
                  <span
                    key={i}
                    className={`w-3 h-3 rounded-full transition-all duration-150 ${filled ? "bg-primary scale-110" : "bg-neutral-300"} ${shake ? "animate-bounce" : ""}`}
                    style={shake ? { animationDelay: `${i * 30}ms`, animationDuration: "380ms" } : {}}
                    data-filled={filled ? "true" : "false"}
                  />
                );
              })}
            </div>
            <input
              ref={inputRef}
              id="finance-pin-input"
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d*"
              maxLength={6}
              value={pin}
              onChange={handlePinChange}
              onKeyDown={handleKeyDown}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer text-center"
              data-testid="finance-pin-input"
            />
          </div>

          {error ? (
            <p className="text-sm text-rose-600 mb-3 text-center" data-testid="finance-pin-error">{error}</p>
          ) : (
            <p className="text-xs text-neutral-400 mb-3 text-center">Inserisci da 4 a 6 cifre</p>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-1">
            <Button type="button" variant="ghost" className="flex-1 h-11"
                    onClick={() => setOpen(false)} data-testid="finance-pin-cancel">
              Annulla
            </Button>
            <Button type="submit" disabled={busy || pin.length < 4} className="flex-1 h-11"
                    data-testid="finance-pin-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Sblocca
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
